import _ from 'lodash';
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useMemo,
  useState,
} from 'react';

import navConfig from '../constants/navigationLinks';
import useUser from '../hooks/useUser';
import NavigationLink from '../interfaces/NavigationLink';

export interface NavListContextType {
  navigationLinks: NavigationLink[];
  allowedPages: string[];
  onActive: (content: NavigationLink) => void;
  fullScreenMode: boolean;
  setFullScreenMode: (value: boolean) => void;
}

const NavigationLinkContext = createContext<NavListContextType>({
  navigationLinks: [],
  allowedPages: [],
  onActive(content) {},
  fullScreenMode: false,
  setFullScreenMode(value) {},
});

export const NavigationLinkProvider: FC<PropsWithChildren> = ({ children }) => {
  const { userPermissions, subscribedPermissions } = useUser();
  const [fullScreenMode, setFullScreenMode] = useState(false);

  const navigationLinks = useMemo(() => {
    // When the business hasn't been onboarded onto the subscription system
    // (empty list) we treat every module as subscribed and only enforce role
    // permissions — mirrors the fallback in `User.tsx`.
    const treatAllAsSubscribed = subscribedPermissions.length === 0;

    // Filters a subtree down to the items the user can access. Used to resolve
    // the children of a module the user has at least partial access to.
    const getAllowedNavList = (list: NavigationLink[]): NavigationLink[] => {
      return list.reduce((acc, val) => {
        const hasPermission = _.isEmpty(
          _.difference(val.requiredPermissions, userPermissions)
        );
        if (!hasPermission) return acc;
        if (typeof val.content === 'string') acc.push(val);
        if (Array.isArray(val.content)) {
          const content = getAllowedNavList(val.content);
          content.length && acc.push({ ...val, content });
        }
        return acc;
      }, [] as NavigationLink[]);
    };

    // Walks a subtree and gathers every permission a leaf route requires.
    // Parent groups frequently declare `requiredPermissions: []` and delegate
    // the real gating to their children, so the parent's own list alone is
    // not a reliable signal for the subscribed-vs-role distinction.
    const collectLeafPermissions = (item: NavigationLink): string[] => {
      const own = item.requiredPermissions ?? [];
      if (typeof item.content === 'string') return own;
      const fromChildren = item.content.flatMap(collectLeafPermissions);
      return [...own, ...fromChildren];
    };

    // Locked modules are split into two cases so the UI can communicate the
    // remediation path: `notSubscribed` (business needs to purchase) vs
    // `locked` (user's role doesn't grant access). When at least one of the
    // required permissions is subscribed, we treat it as a role-level lock —
    // the user could potentially get access via an admin.
    const lockedFlags = (required: string[]) => {
      const unique = _.uniq(required);
      if (unique.length === 0) return { locked: true };
      const anySubscribed =
        treatAllAsSubscribed ||
        unique.some((p) => subscribedPermissions.includes(p));
      return anySubscribed
        ? { locked: true }
        : { locked: true, notSubscribed: true };
    };

    // Every top-level module is kept. If the user has no accessible route
    // anywhere within a module, it is surfaced in a locked state instead of
    // being hidden.
    return navConfig.map((module): NavigationLink => {
      const hasModulePermission = _.isEmpty(
        _.difference(module.requiredPermissions, userPermissions)
      );

      if (typeof module.content === 'string') {
        return hasModulePermission
          ? module
          : { ...module, ...lockedFlags(module.requiredPermissions) };
      }

      const allowedChildren = hasModulePermission
        ? getAllowedNavList(module.content)
        : [];

      if (allowedChildren.length) {
        return { ...module, content: allowedChildren };
      }

      return {
        ...module,
        content: [],
        ...lockedFlags(collectLeafPermissions(module)),
      };
    });
  }, [userPermissions, subscribedPermissions]);

  const allowedPages = useMemo(() => {
    const getAllowedNavList = (list: NavigationLink[]) => {
      return list.reduce((acc, val) => {
        const hasPermission = _.isEmpty(
          _.difference(val.requiredPermissions, userPermissions)
        );

        if (!hasPermission) return acc;
        if (typeof val.content === 'string') acc.push(val.content);
        if (_.isArray(val.content)) acc.push(...getAllowedNavList(val.content));

        return acc;
      }, [] as string[]);
    };

    return getAllowedNavList(navConfig);
  }, [userPermissions]);

  const handleOnActivePage = (content: NavigationLink) => {
    window.document.title = `${content.label} - Casaretail.in`;
  };

  return (
    <NavigationLinkContext.Provider
      value={{
        navigationLinks,
        allowedPages,
        setFullScreenMode,
        onActive: handleOnActivePage,
        fullScreenMode,
      }}
    >
      {children}
    </NavigationLinkContext.Provider>
  );
};

export default NavigationLinkContext;
